import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CallIcon from '@mui/icons-material/Call';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import RefreshIcon from '@mui/icons-material/Refresh';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAuth } from '../../context/authContext';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import useCustomerExperienceFeedback from '../../hooks/useCustomerExperienceFeedback';
import useEntertainment from '../../hooks/useEntertainment';
import useEntertainmentType from '../../hooks/useEntertainmentType';
import useUser from '../../hooks/useUser';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const STATUS_CONFIG = {
  new: { label: 'Neu', color: 'error', Icon: ErrorOutlineIcon },
  inProgress: { label: 'In Bearbeitung', color: 'warning', Icon: HourglassEmptyIcon },
  resolved: { label: 'Gelöst', color: 'success', Icon: CheckCircleIcon },
};

const VOUCHER_PRESETS = [10, 20, 30, 50];
const SYSTEM_DEFAULT_VOUCHER_LIMIT = 50;

/**
 * Standard response template used when the customer left no written message.
 * Uses the entertainment name for personalisation.
 */
function buildNoMessageStandardText(firstName, entertainmentName) {
  const name = firstName ? `Hallo ${firstName},` : 'Hallo,';
  const ent = entertainmentName || 'uns';
  return `${name}

danke, dass du dir die Zeit genommen hast, Feedback zu deinem Besuch in der ${ent} zu geben.

Was hat dich gestört oder nicht gestimmt? Schreib uns gerne kurz – wir kümmern uns darum.`;
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function redactEmail(email) {
  if (!email) return '—';
  const at = email.indexOf('@');
  if (at < 1) return email;
  const local = email.substring(0, at);
  const domain = email.substring(at);
  const visible = Math.min(2, local.length);
  return local.substring(0, visible) + '***' + domain;
}

function CallNoteDialog({ open, onClose, onConfirm, loading }) {
  const noteRef = useRef(null);

  const handleConfirm = useCallback(() => {
    const note = noteRef.current?.value?.trim() || '';
    onConfirm(note);
  }, [onConfirm]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Anruf dokumentieren</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Halte kurz fest, was im Gespräch kommuniziert wurde.
        </Typography>
        <TextField
          inputRef={noteRef}
          label='Gesprächsnotiz (optional)'
          multiline
          minRows={3}
          fullWidth
          placeholder='z.B. Kunde ist zufrieden mit Lösung, erhält Gutschein per Mail...'
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          variant='outlined'
          startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
          onClick={handleConfirm}
          disabled={loading}
        >
          Als angerufen markieren
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CustomerExperienceFeedbackView() {
  const authCtx = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { getCustomerFeedback, updateStatus, updateCustomerCalled, generateAiSuggestion, sendResponse } =
    useCustomerExperienceFeedback();
  const { getEntertainments } = useEntertainment();
  const { getEntertainmentTypes, updateAiPrompt: saveAiPrompt } = useEntertainmentType();
  const { getMyBranches } = useUser();

  const canRead = useMemo(
    () =>
      authCtx.checkRight('customerExperienceFeedback:read') ||
      authCtx.checkRight('customerExperienceFeedback:read:own'),
    [authCtx]
  );
  const canRespond = useMemo(
    () =>
      authCtx.checkRight('customerExperienceFeedback:respond') ||
      authCtx.checkRight('customerExperienceFeedback:respond:own'),
    [authCtx]
  );
  const canEditAiPrompt = useMemo(
    () => authCtx.checkRight('entertainmentType:update'),
    [authCtx]
  );
  const canApproveHighValueVoucher = useMemo(
    () => authCtx.checkRight('customerExperienceFeedback:special:approveHighValueVoucher'),
    [authCtx]
  );

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('new');
  const [entertainmentFilter, setEntertainmentFilter] = useState('');
  const [entertainments, setEntertainments] = useState([]);
  const [assignedBranchIds, setAssignedBranchIds] = useState(null); // null = not yet loaded
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoadingItemId, setAiLoadingItemId] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [calledLoading, setCalledLoading] = useState(false);

  const [callNoteDialogOpen, setCallNoteDialogOpen] = useState(false);
  const [phoneVisible, setPhoneVisible] = useState(false);
  const [bookingModalId, setBookingModalId] = useState(null);

  const [voucherValue, setVoucherValue] = useState('');
  const [customVoucherValue, setCustomVoucherValue] = useState('');
  const [highValueApprovalConfirmed, setHighValueApprovalConfirmed] = useState(false);
  const [responseDisplay, setResponseDisplay] = useState('');

  // AI prompt editor state
  const [entertainmentTypes, setEntertainmentTypes] = useState([]);
  const [aiPromptDialogOpen, setAiPromptDialogOpen] = useState(false);
  const [aiPromptSaving, setAiPromptSaving] = useState(false);
  const [aiPromptTypeId, setAiPromptTypeId] = useState('');
  const aiPromptRef = useRef(null);

  const responseRef = useRef(null);
  const didMount = useRef(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(33);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);

  // Computed voucher limit state — derived outside JSX so the send button can gate on them
  const resolvedVoucherNum =
    voucherValue === 'custom'
      ? Number(customVoucherValue) || 0
      : voucherValue ? Number(voucherValue) : 0;
  const effectiveVoucherLimit =
    selected?.entertainmentId?.maxCourtesyVoucherAmount ?? SYSTEM_DEFAULT_VOUCHER_LIMIT;
  const isVoucherAboveLimit = resolvedVoucherNum > 0 && resolvedVoucherNum > effectiveVoucherLimit;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCustomerFeedback({
        status: statusFilter || null,
        entertainmentId: entertainmentFilter || null,
      });
      if (result) {
        setItems(result.items || []);
        setTotal(result.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [getCustomerFeedback, statusFilter, entertainmentFilter]);

  useEffect(() => {
    if (!canRead) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, entertainmentFilter, canRead]);

  useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;

    const init = async () => {
      // Fetch the current user's assigned branches, all entertainments, and entertainment types in parallel
      const [branchResult, allEntertainments, allEntertainmentTypes] = await Promise.all([
        getMyBranches().catch(() => null),
        getEntertainments().catch(() => null),
        getEntertainmentTypes().catch(() => null),
      ]);

      const branchIds = branchResult?.branchIds || [];
      setAssignedBranchIds(branchIds);

      if (allEntertainments) {
        // When the user is scoped to specific branches, filter the dropdown to those branches only
        const filtered =
          branchIds.length > 0
            ? allEntertainments.filter(e => branchIds.includes(e.branch?.toString() || e.branch))
            : allEntertainments;
        setEntertainments(filtered);
      }

      if (allEntertainmentTypes) {
        setEntertainmentTypes(Array.isArray(allEntertainmentTypes) ? allEntertainmentTypes : []);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = useCallback(item => {
    setSelected(item);
    setPhoneVisible(false);

    const entertainmentName =
      item.entertainmentId?.longName || item.entertainmentId?.entertainmentName || '';

    let initialResponse = '';
    if (item.response) {
      initialResponse = item.response;
    } else if (item.message) {
      // Has customer text: pre-fill from AI suggestion if available
      initialResponse = item.aiSuggestion?.responseText || '';
    } else {
      // No customer text: always pre-fill with standard text
      initialResponse = buildNoMessageStandardText(item.customerFirstName, entertainmentName);
    }

    setResponseDisplay(initialResponse);

    // Only pre-fill voucher value if there's a customer message and AI suggests one
    const suggestedAmount =
      item.message && item.aiSuggestion?.voucherRecommended && item.aiSuggestion?.voucherAmount
        ? String(item.aiSuggestion.voucherAmount)
        : '';
    setVoucherValue(item.courtesyVoucherValue ? String(item.courtesyVoucherValue) : suggestedAmount);
    setCustomVoucherValue('');
    setHighValueApprovalConfirmed(false);
    if (isMobile) setShowDetail(true);
  }, [isMobile]);

  const handleStatusChange = useCallback(
    async (feedbackId, newStatus) => {
      await updateStatus({ feedbackId, status: newStatus });
      const update = { status: newStatus };
      setItems(prev => prev.map(i => (i._id === feedbackId ? { ...i, ...update } : i)));
      if (selected?._id === feedbackId) setSelected(prev => ({ ...prev, ...update }));
      toast.success('Status aktualisiert');
    },
    [updateStatus, selected]
  );

  const handleCalledConfirm = useCallback(
    async (callNote) => {
      if (!selected) return;
      setCalledLoading(true);
      try {
        const result = await updateCustomerCalled({
          feedbackId: selected._id,
          customerCalled: true,
          callNote: callNote || null,
        });
        const update = {
          customerCalled: true,
          callNote: result?.callNote || callNote || selected.callNote,
          calledAt: result?.calledAt || new Date().toISOString(),
        };
        setItems(prev => prev.map(i => (i._id === selected._id ? { ...i, ...update } : i)));
        setSelected(prev => ({ ...prev, ...update }));
        setCallNoteDialogOpen(false);
        toast.success('Anruf dokumentiert');
      } finally {
        setCalledLoading(false);
      }
    },
    [updateCustomerCalled, selected]
  );

  const handleUnmarkCalled = useCallback(async () => {
    if (!selected) return;
    setCalledLoading(true);
    try {
      await updateCustomerCalled({ feedbackId: selected._id, customerCalled: false });
      const update = { customerCalled: false, callNote: null, calledAt: null };
      setItems(prev => prev.map(i => (i._id === selected._id ? { ...i, ...update } : i)));
      setSelected(prev => ({ ...prev, ...update }));
    } finally {
      setCalledLoading(false);
    }
  }, [updateCustomerCalled, selected]);

  const applyAiSuggestion = useCallback(
    (suggestion) => {
      if (!suggestion) return;
      if (suggestion.responseText) {
        setResponseDisplay(suggestion.responseText);
        if (responseRef.current) responseRef.current.value = suggestion.responseText;
      }
      if (suggestion.voucherRecommended && suggestion.voucherAmount) {
        setVoucherValue(String(suggestion.voucherAmount));
      }
    },
    []
  );

  const handleGenerateAi = useCallback(
    async (feedbackId) => {
      setAiLoadingItemId(feedbackId);
      if (selected?._id === feedbackId) setAiLoading(true);
      try {
        const result = await generateAiSuggestion({ feedbackId });
        if (result?.aiSuggestion) {
          const s = result.aiSuggestion;
          const update = { aiSuggestion: s };
          setItems(prev => prev.map(i => (i._id === feedbackId ? { ...i, ...update } : i)));
          if (selected?._id === feedbackId) {
            setSelected(prev => ({ ...prev, ...update }));
            // Auto-apply only when the response field is still empty
            const currentText = responseRef.current?.value?.trim();
            if (!currentText) {
              applyAiSuggestion(s);
            }
          }
          toast.success('KI-Vorschlag generiert');
        }
      } finally {
        setAiLoadingItemId(null);
        setAiLoading(false);
      }
    },
    [generateAiSuggestion, selected, applyAiSuggestion]
  );

  // Resolves the EntertainmentType document matching the selected feedback's entertainment tags
  const selectedEntertainmentType = useMemo(() => {
    if (!selected || entertainmentTypes.length === 0) return null;
    const entertainmentName = (selected.entertainmentId?.entertainmentName || '').toLowerCase();
    return entertainmentTypes.find(et => et.name.toLowerCase() === entertainmentName) || null;
  }, [selected, entertainmentTypes]);

  const handleSaveAiPrompt = useCallback(async () => {
    if (!aiPromptTypeId) return;
    setAiPromptSaving(true);
    try {
      const newPrompt = aiPromptRef.current?.value ?? '';
      const updated = await saveAiPrompt({
        entertainmentTypeId: aiPromptTypeId,
        experienceFeedbackAiPrompt: newPrompt,
      });
      setEntertainmentTypes(prev =>
        prev.map(et => (et._id === updated._id ? updated : et))
      );
      toast.success('KI-Prompt gespeichert');
      setAiPromptDialogOpen(false);
    } catch {
      toast.error('Fehler beim Speichern des KI-Prompts');
    } finally {
      setAiPromptSaving(false);
    }
  }, [saveAiPrompt, aiPromptTypeId]);

  const handleSendResponse = useCallback(async () => {
    const responseText = responseRef.current?.value?.trim() || responseDisplay.trim();
    if (!responseText) {
      toast.error('Bitte einen Antworttext eingeben');
      return;
    }

    const resolvedVoucherValue =
      voucherValue === 'custom'
        ? Number(customVoucherValue) || null
        : voucherValue
        ? Number(voucherValue)
        : null;

    // Client-side guard: block send if voucher exceeds limit without approval
    if (resolvedVoucherValue && resolvedVoucherValue > 0) {
      const limit = selected?.entertainmentId?.maxCourtesyVoucherAmount ?? SYSTEM_DEFAULT_VOUCHER_LIMIT;
      if (resolvedVoucherValue > limit) {
        if (!canApproveHighValueVoucher) {
          toast.error(`Gutscheinwert (${resolvedVoucherValue} €) überschreitet das Limit von ${limit} €.`);
          return;
        }
        if (!highValueApprovalConfirmed) {
          toast.error('Bitte erteile die Sonderfreigabe, bevor du die Antwort sendest.');
          return;
        }
      }
    }

    setSendLoading(true);
    try {
      const result = await sendResponse({
        feedbackId: selected._id,
        responseText,
        courtesyVoucherValue: resolvedVoucherValue,
      });
      const updatedItem = {
        ...selected,
        response: responseText,
        responseEmailSentAt: new Date().toISOString(),
        status: 'resolved',
        courtesyVoucherCode: result?.courtesyVoucherCode || selected.courtesyVoucherCode,
        courtesyVoucherValue: result?.courtesyVoucherValue || resolvedVoucherValue,
      };
      setSelected(updatedItem);
      setItems(prev => prev.map(i => (i._id === selected._id ? updatedItem : i)));
      toast.success('Antwort gesendet und Feedback als gelöst markiert');
    } catch (err) {
      if (err?.code === 'VOUCHER_EXCEEDS_LIMIT') {
        toast.error(`Gutscheinwert überschreitet das erlaubte Limit von ${err.limit ?? effectiveVoucherLimit} €. Sonderfreigabe erforderlich.`);
      } else if (err?.code === 'GIFTED_VOUCHER_MONTHLY_BUDGET_EXCEEDED') {
        const budgetData = err?.data?.data || err?.data || {};
        const remaining = Number(budgetData.remaining || 0).toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        const requested = Number(budgetData.requested || 0).toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        toast.error(`Monatskontingent überschritten: verfügbar ${remaining} €, angefragt ${requested} €.`);
      } else {
        throw err;
      }
    } finally {
      setSendLoading(false);
    }
  }, [sendResponse, selected, responseDisplay, voucherValue, customVoucherValue, canApproveHighValueVoucher, highValueApprovalConfirmed, effectiveVoucherLimit]);

  const handleResizeStart = e => {
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = leftPanelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResizeMove = useCallback(
    e => {
      if (!isResizing || !containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const deltaX = e.clientX - resizeStartX.current;
      const deltaPercentage = (deltaX / containerWidth) * 100;
      const minWidthPercentage = Math.max(25, (280 / containerWidth) * 100);
      const newWidth = Math.max(
        minWidthPercentage,
        Math.min(80, resizeStartWidth.current + deltaPercentage)
      );

      setLeftPanelWidth(newWidth);
    },
    [isResizing]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  if (!canRead) {
    return <Navigate to='/welcome' replace />;
  }

  const renderListItem = (item) => {
    const { label: statusLabel, color: statusColor } = STATUS_CONFIG[item.status] || STATUS_CONFIG.new;
    const isSelected = selected?._id === item._id;
    const isAiLoading = aiLoadingItemId === item._id;

    return (
      <Box
        key={item._id}
        onClick={() => handleSelect(item)}
        sx={{
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <ThumbDownAltIcon fontSize='small' sx={{ color: 'error.main', flexShrink: 0 }} />
            <Typography variant='subtitle2' noWrap sx={{ minWidth: 0 }}>
              {item.customerFirstName || 'Kunde'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              flexShrink: 1,
              flexWrap: 'wrap',
              minWidth: 0,
              maxWidth: '55%',
            }}
          >
            {item.customerCalled && (
              <Tooltip title='Angerufen'>
                <PhoneInTalkIcon fontSize='small' sx={{ color: 'success.main' }} />
              </Tooltip>
            )}
            <Chip
              label={statusLabel}
              color={statusColor}
              size='small'
              sx={{
                height: 20,
                fontSize: '0.65rem',
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          </Box>
        </Box>

        <Typography variant='caption' color='text.secondary' display='block' noWrap>
          {item.entertainmentId?.longName || item.entertainmentId?.entertainmentName || '—'}
        </Typography>

        {item.message ? (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mt: 0.5, fontSize: '0.75rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {item.message}
          </Typography>
        ) : (
          <Typography variant='caption' color='text.disabled' sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
            Kein schriftliches Feedback
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}>
          <Typography variant='caption' color='text.secondary'>
            {formatDate(item.meta?.createdAt)}
          </Typography>
          {canRespond && item.message && (
            <Tooltip title={item.aiSuggestion ? 'KI-Vorschlag neu generieren' : 'KI-Vorschlag generieren'}>
              <IconButton
                size='small'
                onClick={e => { e.stopPropagation(); handleGenerateAi(item._id); }}
                disabled={isAiLoading}
                sx={{ p: 0.25 }}
              >
                {isAiLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <AutoAwesomeIcon fontSize='small' sx={{ color: item.aiSuggestion ? 'success.main' : 'action.active' }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const renderList = () => (
    <Box
      sx={{
        flex: { xs: '1 1 100%', md: `0 0 ${leftPanelWidth}%` },
        width: { xs: '100%', md: `${leftPanelWidth}%` },
        minWidth: { xs: '100%', md: 280 },
        overflow: 'auto',
        display: isMobile && showDetail ? 'none' : 'flex',
        flexDirection: 'column',
        transition: isResizing ? 'none' : 'width 0.3s ease, flex-basis 0.3s ease',
      }}
    >
      {/* Filters */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, v) => { if (v !== null) setStatusFilter(v); }}
          size='small'
          fullWidth
          sx={{
            mb: 1.5,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            '& .MuiToggleButtonGroup-grouped': {
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              flex: '1 1 90px',
              minWidth: 0,
              px: 0.75,
            },
          }}
        >
          <ToggleButton value='new' sx={{ fontSize: '0.72rem' }}>Neu</ToggleButton>
          <ToggleButton value='inProgress' sx={{ fontSize: '0.72rem' }}>Bearbeitung</ToggleButton>
          <ToggleButton value='resolved' sx={{ fontSize: '0.72rem' }}>Gelöst</ToggleButton>
        </ToggleButtonGroup>

        <FormControl fullWidth size='small'>
          <InputLabel id='ent-filter-label'>Entertainment</InputLabel>
          <Select
            labelId='ent-filter-label'
            value={entertainmentFilter}
            label='Entertainment'
            onChange={e => setEntertainmentFilter(e.target.value)}
          >
            <MenuItem value=''>Alle</MenuItem>
            {entertainments.map(e => (
              <MenuItem key={e._id} value={e._id}>
                {e.longName || e.entertainmentName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* List header */}
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant='caption' color='text.secondary'>
          {total} negatives Feedback
        </Typography>
        <IconButton size='small' onClick={load} disabled={loading}>
          {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize='small' />}
        </IconButton>
      </Box>

      {items.length === 0 && !loading && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant='body2' color='text.secondary'>Keine Einträge</Typography>
        </Box>
      )}

      {items.map(renderListItem)}
    </Box>
  );

  const renderDetail = () => {
    if (!selected) {
      if (isMobile && showDetail) return null;
      return (
        <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant='body2' color='text.secondary'>
            Wähle einen Eintrag aus der Liste aus
          </Typography>
        </Box>
      );
    }

    const { label: statusLabel, color: statusColor } = STATUS_CONFIG[selected.status] || STATUS_CONFIG.new;
    const alreadySent = Boolean(selected.responseEmailSentAt);
    const resolvedVoucherValueForDisplay =
      voucherValue === 'custom' ? customVoucherValue : voucherValue;

    return (
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: isMobile && !showDetail ? 'none' : 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Mobile back button */}
        {isMobile && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Button size='small' onClick={() => setShowDetail(false)}>
              ← Zurück zur Liste
            </Button>
          </Box>
        )}

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant='h6'>
                {selected.customerFirstName || 'Kunde'}{' '}
                <Typography component='span' variant='body2' color='text.secondary'>
                  ({selected.customerEmail})
                </Typography>
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {selected.entertainmentId?.longName || '—'} · {formatDate(selected.meta?.createdAt)}
              </Typography>
              {selected.bookingNumber && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Buchung #{selected.bookingNumber}
                  </Typography>
                  {selected.bookingId && (
                    <Tooltip title='Buchung öffnen'>
                      <IconButton
                        size='small'
                        sx={{ p: 0.25 }}
                        onClick={() => setBookingModalId(
                          typeof selected.bookingId === 'object' ? selected.bookingId._id : selected.bookingId
                        )}
                      >
                        <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
            <FormControl size='small' sx={{ minWidth: 160 }}>
              <InputLabel id='status-change-label'>Status</InputLabel>
              <Select
                labelId='status-change-label'
                value={selected.status}
                label='Status'
                onChange={e => handleStatusChange(selected._id, e.target.value)}
                disabled={!canRespond}
              >
                <MenuItem value='new'>Neu</MenuItem>
                <MenuItem value='inProgress'>In Bearbeitung</MenuItem>
                <MenuItem value='resolved'>Gelöst</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Grid container spacing={2}>
            {/* Single column: all content stacks vertically */}
            <Grid item xs={12}>

              {/* Customer message */}
              <Typography variant='subtitle2' sx={{ mb: 1 }}>Kundennachricht</Typography>
              {selected.message ? (
                <Paper
                  variant='outlined'
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: 'background.default',
                    borderColor: 'error.light',
                  }}
                >
                  <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
                    Eingegangen: {formatDate(selected.messageSubmittedAt)}
                  </Typography>
                  <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                    {selected.message}
                  </Typography>
                </Paper>
              ) : (
                <Alert severity='info' sx={{ mb: 2 }}>
                  Kein schriftliches Feedback vorhanden
                </Alert>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* AI section – generate when no suggestion yet; regenerate button when already generated */}
              {canRespond && selected.message && !alreadySent && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    startIcon={aiLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                    variant={selected.aiSuggestion ? 'outlined' : 'contained'}
                    size='small'
                    onClick={() => handleGenerateAi(selected._id)}
                    disabled={aiLoading}
                    sx={{ flex: '1 1 auto' }}
                  >
                    {selected.aiSuggestion ? 'KI-Vorschlag neu generieren' : 'KI-Vorschlag generieren'}
                  </Button>
                  {canEditAiPrompt && (
                    <Tooltip title='KI-Prompt bearbeiten'>
                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setAiPromptTypeId(selectedEntertainmentType?._id || '');
                          setAiPromptDialogOpen(true);
                        }}
                      >
                        Prompt
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              )}

              {/* AI suggestion result card */}
              {canRespond && selected.message && selected.aiSuggestion && (
                <Box sx={{ mb: 2 }}>
                  <Paper
                    variant='outlined'
                    sx={{ p: 2, mb: 1.5, bgcolor: 'primary.50', borderColor: 'primary.light' }}
                  >
                    <Typography
                      variant='caption'
                      color='primary.main'
                      sx={{ fontWeight: 600, display: 'block', mb: 1 }}
                    >
                      KI-Vorschlag ({formatDate(selected.aiSuggestion.generatedAt)})
                    </Typography>

                    {selected.message && selected.message.length > 200 && selected.aiSuggestion.summary && (
                      <Alert
                        severity='info'
                        sx={{ mb: 1.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
                      >
                        <strong>Zusammenfassung:</strong> {selected.aiSuggestion.summary}
                      </Alert>
                    )}

                    {selected.aiSuggestion.callRecommended && (
                      <Chip
                        icon={<CallIcon fontSize='small' />}
                        label='Telefonat empfohlen'
                        color='warning'
                        size='small'
                        sx={{
                          mb: 1.5,
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                    )}

                    {selected.aiSuggestion.voucherRecommended && (
                      <Alert
                        severity='success'
                        sx={{ mb: 1.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
                      >
                        <strong>Gutschein empfohlen:</strong> {selected.aiSuggestion.voucherAmount} € —{' '}
                        {selected.aiSuggestion.voucherReason}
                      </Alert>
                    )}

                    {selected.aiSuggestion.responseText && (
                      <Box>
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
                          Vorgeschlagener Antworttext:
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            whiteSpace: 'pre-wrap',
                            fontStyle: 'italic',
                            color: 'text.secondary',
                            borderRadius: 1,
                            p: 1,
                            bgcolor: 'action.hover',
                            mb: 1,
                          }}
                        >
                          {selected.aiSuggestion.responseText}
                        </Typography>
                        <Button
                          size='small'
                          variant='contained'
                          onClick={() => applyAiSuggestion(selected.aiSuggestion)}
                        >
                          Vorschlag übernehmen
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Response composer */}
              {canRespond && (
                <Box>
                  <Typography variant='subtitle2' sx={{ mb: 1.5 }}>
                    Antwort an den Kunden
                  </Typography>

                  {alreadySent ? (
                    <Alert severity='success' icon={<MarkEmailReadIcon />} sx={{ mb: 2 }}>
                      Antwort am {formatDate(selected.responseEmailSentAt)} gesendet.
                    </Alert>
                  ) : null}

                  {/* Response area: text field + fixed sign-off in one outlined container */}
                  <Paper variant='outlined' sx={{ mb: 2, overflow: 'hidden' }}>
                    <TextField
                      key={selected._id}
                      inputRef={responseRef}
                      multiline
                      minRows={5}
                      fullWidth
                      variant='standard'
                      defaultValue={responseDisplay}
                      disabled={alreadySent}
                      placeholder='Schreibe hier die Antwort an den Kunden...'
                      sx={{
                        px: 1.75,
                        pt: 1.5,
                        pb: 1,
                        '& .MuiInput-underline:before': { display: 'none' },
                        '& .MuiInput-underline:after': { display: 'none' },
                      }}
                    />
                    <Divider />
                    <Box sx={{ px: 1.75, py: 1, bgcolor: 'action.hover' }}>
                      <Typography variant='caption' color='text.disabled' sx={{ display: 'block', fontStyle: 'italic' }}>
                        Viele Grüße
                      </Typography>
                      <Typography variant='caption' color='text.disabled' sx={{ fontStyle: 'italic' }}>
                        Dein {selected.entertainmentId?.longName || selected.entertainmentId?.entertainmentName || 'FunZone'} Team
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Voucher value selection – only offered when the customer left a message */}
                  {!alreadySent && selected.message && (() => {
                    const effectiveLimit = effectiveVoucherLimit;
                    const resolvedNum = resolvedVoucherNum;
                    const isAboveLimit = isVoucherAboveLimit;

                    return (
                      <Box sx={{ mb: 2 }}>
                        <Stack
                          direction='row'
                          alignItems='center'
                          spacing={1}
                          useFlexGap
                          sx={{ mb: 1, flexWrap: 'wrap' }}
                        >
                          <Typography variant='caption' color='text.secondary'>
                            Kulanzgutschein (wird beim Senden automatisch erstellt und der Mail angehängt)
                          </Typography>
                          <Typography variant='caption' color='text.disabled'>
                            · Limit ohne Freigabe: <strong>{effectiveLimit} €</strong>
                          </Typography>
                        </Stack>
                        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                          <Chip
                            label='Kein Gutschein'
                            variant={!voucherValue ? 'filled' : 'outlined'}
                            color='default'
                            onClick={() => { setVoucherValue(''); setCustomVoucherValue(''); setHighValueApprovalConfirmed(false); }}
                            sx={{ cursor: 'pointer', maxWidth: '100%' }}
                          />
                          {VOUCHER_PRESETS.map(v => {
                            const aboveForPreset = v > effectiveLimit;
                            const isSelected = voucherValue === String(v);
                            const isDisabled = aboveForPreset && !canApproveHighValueVoucher;
                            return (
                              <Tooltip
                                key={v}
                                title={isDisabled ? `Über dem Limit von ${effectiveLimit} € – Sonderfreigabe erforderlich` : ''}
                              >
                                <span>
                                  <Chip
                                    label={`${v} €`}
                                    variant={isSelected ? 'filled' : 'outlined'}
                                    color={isSelected ? (aboveForPreset ? 'warning' : 'primary') : 'default'}
                                    onClick={() => { if (!isDisabled) { setVoucherValue(String(v)); setCustomVoucherValue(''); setHighValueApprovalConfirmed(false); } }}
                                    disabled={isDisabled}
                                    sx={{ cursor: isDisabled ? 'not-allowed' : 'pointer', maxWidth: '100%' }}
                                  />
                                </span>
                              </Tooltip>
                            );
                          })}
                          <Chip
                            label='Benutzerdefiniert'
                            variant={voucherValue === 'custom' ? 'filled' : 'outlined'}
                            color={voucherValue === 'custom' ? 'secondary' : 'default'}
                            onClick={() => { setVoucherValue('custom'); setHighValueApprovalConfirmed(false); }}
                            sx={{
                              cursor: 'pointer',
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        </Stack>

                        {voucherValue === 'custom' && (
                          <TextField
                            label='Betrag (€)'
                            type='number'
                            size='small'
                            value={customVoucherValue}
                            onChange={e => {
                              const maxAllowed = canApproveHighValueVoucher ? 100 : effectiveLimit;
                              const val = Math.min(maxAllowed, Math.max(1, Number(e.target.value)));
                              setCustomVoucherValue(String(val));
                              setHighValueApprovalConfirmed(false);
                            }}
                            sx={{ mt: 1, width: 140 }}
                            inputProps={{ min: 1, max: canApproveHighValueVoucher ? 100 : effectiveLimit }}
                            helperText={!canApproveHighValueVoucher ? `Max. ${effectiveLimit} €` : undefined}
                          />
                        )}

                        {/* High-value approval confirmation */}
                        {isAboveLimit && canApproveHighValueVoucher && (
                          <Alert
                            severity='warning'
                            sx={{
                              mt: 1.5,
                              alignItems: 'flex-start',
                              '& .MuiAlert-message': { minWidth: 0 },
                              '& .MuiAlert-action': {
                                pl: { xs: 0, sm: 2 },
                                ml: { xs: 0, sm: 'auto' },
                                mt: { xs: 1, sm: 0 },
                              },
                            }}
                            action={
                              <Button
                                size='small'
                                color='inherit'
                                variant={highValueApprovalConfirmed ? 'contained' : 'outlined'}
                                onClick={() => setHighValueApprovalConfirmed(v => !v)}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {highValueApprovalConfirmed ? 'Freigabe erteilt ✓' : 'Freigabe erteilen'}
                              </Button>
                            }
                          >
                            <strong>{resolvedNum} €</strong> überschreitet das Limit von {effectiveLimit} €.
                            Sonderfreigabe erforderlich.
                          </Alert>
                        )}

                        {isAboveLimit && !canApproveHighValueVoucher && (
                          <Alert severity='error' sx={{ mt: 1.5 }}>
                            Dieser Betrag ({resolvedNum} €) überschreitet das Limit von {effectiveLimit} €. Du benötigst das Recht <em>approveHighValueVoucher</em>.
                          </Alert>
                        )}
                      </Box>
                    );
                  })()}

                  {!alreadySent && (
                    <Button
                      variant='contained'
                      color='primary'
                      startIcon={sendLoading ? <CircularProgress size={16} color='inherit' /> : <CallMadeIcon />}
                      onClick={handleSendResponse}
                      disabled={
                        sendLoading ||
                        (isVoucherAboveLimit && (!canApproveHighValueVoucher || !highValueApprovalConfirmed))
                      }
                    >
                      Antwort senden
                    </Button>
                  )}
                </Box>
              )}

              {/* Courtesy voucher summary when resolved */}
              {alreadySent && (selected.courtesyVoucherCode || selected.courtesyVoucherValue) && (
                <Paper variant='outlined' sx={{ p: 2, mt: 2 }}>
                  <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
                    Kulanzgutschein
                  </Typography>
                  {selected.courtesyVoucherCode && (
                    <Typography variant='body2'>
                      Code: <strong>{selected.courtesyVoucherCode}</strong>
                    </Typography>
                  )}
                  {selected.courtesyVoucherValue && (
                    <Typography variant='body2'>
                      Wert: <strong>{selected.courtesyVoucherValue} €</strong>
                    </Typography>
                  )}
                </Paper>
              )}
            </Grid>

            {/* Call section — only rendered when a phone number is available */}
            {(selected.customerPhone || selected.bookingId?.phone) && (
            <Grid item xs={12}>
              <Paper variant='outlined' sx={{ p: 2 }}>
                <Typography variant='subtitle2' sx={{ mb: 1.5 }}>Telefonkontakt</Typography>

                {(() => {
                  const phone = selected.customerPhone || selected.bookingId?.phone;
                  return (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                        {phone && (
                          <Button
                            variant='outlined'
                            size='small'
                            startIcon={<PhoneIcon />}
                            onClick={() => setPhoneVisible(v => !v)}
                            sx={{
                              flex: '1 1 180px',
                              minWidth: 0,
                              maxWidth: '100%',
                              justifyContent: 'flex-start',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {phoneVisible ? phone : 'Nummer anzeigen'}
                          </Button>
                        )}

                        {selected.customerCalled ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label='Angerufen'
                            color='success'
                            size='small'
                            sx={{
                              flex: '0 1 auto',
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        ) : (
                          canRespond && (
                            <Button
                              variant='contained'
                              color='success'
                              size='small'
                              startIcon={
                                calledLoading ? (
                                  <CircularProgress size={16} color='inherit' />
                                ) : (
                                  <CheckCircleOutlineIcon />
                                )
                              }
                              onClick={() => setCallNoteDialogOpen(true)}
                              disabled={calledLoading}
                              sx={{ flex: '1 1 140px' }}
                            >
                              Angerufen
                            </Button>
                          )
                        )}
                      </Box>

                      {selected.customerCalled && (
                        <Box>
                          {selected.calledAt && (
                            <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
                              {formatDate(selected.calledAt)}
                            </Typography>
                          )}
                          {selected.callNote && (
                            <Paper variant='outlined' sx={{ p: 1.5, mb: 1, bgcolor: 'background.default' }}>
                              <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
                                Gesprächsnotiz:
                              </Typography>
                              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                                {selected.callNote}
                              </Typography>
                            </Paper>
                          )}
                          {canRespond && (
                            <Button
                              variant='text'
                              size='small'
                              color='inherit'
                              onClick={handleUnmarkCalled}
                              disabled={calledLoading}
                              sx={{ color: 'text.secondary', fontSize: '0.72rem' }}
                            >
                              Als nicht angerufen zurücksetzen
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Paper>
            </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        ...viewRootTextPrimarySx,
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {renderList()}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            width: '4px',
            cursor: 'col-resize',
            bgcolor: 'transparent',
            flexShrink: 0,
            position: 'relative',
            '&:hover': {
              bgcolor: 'primary.main',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-2px',
              width: '8px',
              height: '100%',
              cursor: 'col-resize',
            },
          }}
          onMouseDown={handleResizeStart}
        />
        {renderDetail()}
      </Box>

      <CallNoteDialog
        open={callNoteDialogOpen}
        onClose={() => setCallNoteDialogOpen(false)}
        onConfirm={handleCalledConfirm}
        loading={calledLoading}
      />

      <BookingDetailsModal
        open={!!bookingModalId}
        onClose={() => setBookingModalId(null)}
        bookingId={bookingModalId}
      />

      {/* AI Prompt editor dialog – only shown to users with entertainmentType:editAiPrompt */}
      <Dialog
        open={aiPromptDialogOpen}
        onClose={() => setAiPromptDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>KI-Prompt bearbeiten</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Dieser Prompt wird der KI als zusätzlicher Kontext mitgegeben, wenn du Vorschläge für diesen Entertainment-Typ generierst.
          </Typography>
          <FormControl fullWidth size='small' sx={{ mb: 2 }}>
            <InputLabel id='ai-prompt-type-label'>Entertainment-Typ</InputLabel>
            <Select
              labelId='ai-prompt-type-label'
              value={aiPromptTypeId}
              label='Entertainment-Typ'
              onChange={e => setAiPromptTypeId(e.target.value)}
            >
              {entertainmentTypes.map(et => (
                <MenuItem key={et._id} value={et._id}>{et.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            key={aiPromptTypeId}
            inputRef={aiPromptRef}
            label='KI-Prompt'
            multiline
            minRows={5}
            fullWidth
            defaultValue={entertainmentTypes.find(et => et._id === aiPromptTypeId)?.experienceFeedbackAiPrompt || ''}
            placeholder='z.B. Besonderheiten, typische Beschwerden, spezifische Kulanzregeln für diesen Entertainment-Typ...'
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setAiPromptDialogOpen(false)} disabled={aiPromptSaving}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleSaveAiPrompt}
            disabled={aiPromptSaving || !aiPromptTypeId}
            startIcon={aiPromptSaving ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
